/**
 * NIL Go clearance on a deal, as both sides see it.
 *
 * A deal worth $600 or more has to be cleared by the NIL Go clearinghouse before its content can
 * go live. The athlete files it within 5 business days of the contract being signed (the deal
 * agreed); the clearinghouse confirms it with a confirmation number. Their school is told
 * alongside.
 */

export const NIL_GO_THRESHOLD = 600
export const NIL_GO_BUSINESS_DAYS = 5

const DAY = 86_400_000

/** `days` weekdays after `start` */
export function addBusinessDays(start: Date, days: number): Date {
  const d = new Date(start)
  let added = 0
  while (added < days) {
    d.setDate(d.getDate() + 1)
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) added++
  }
  return d
}

export type NilComplianceInput = {
  amount: number | null | undefined
  /** Set by hand on deals that need it whatever the amount */
  complianceNeeded?: boolean | null
  /** Whether the deal is agreed (the contract signed) */
  agreed: boolean
  executedAt: string | Date | null | undefined
  nilGoStatus: string | null | undefined
  nilGoConfirmationNumber?: string | null
  nilGoSubmittedAt?: string | Date | null
  schoolStatus?: string | null
}

export type NilCompliance = {
  required: boolean
  /**
   * not_required: under $600 · waiting: the deal isn't signed yet · due: the athlete has until
   * the deadline · overdue: past it · cleared: confirmed by the clearinghouse
   */
  state: 'not_required' | 'waiting' | 'due' | 'overdue' | 'cleared'
  /** Started filing, without a confirmation yet */
  inProgress: boolean
  deadline: string | null
  /** Whole days until the deadline (negative once overdue) */
  daysLeft: number | null
  confirmationNumber: string | null
  clearedAt: string | null
  schoolReported: boolean
}

const iso = (v: string | Date | null | undefined) => (v ? new Date(v as any).toISOString() : null)

export function nilCompliance(input: NilComplianceInput, now = new Date()): NilCompliance {
  const required = !!input.complianceNeeded || Number(input.amount ?? 0) >= NIL_GO_THRESHOLD
  const confirmation =
    input.nilGoConfirmationNumber && input.nilGoConfirmationNumber !== 'IN_PROGRESS'
      ? input.nilGoConfirmationNumber
      : null
  const cleared = input.nilGoStatus === 'confirmed' || !!confirmation
  const schoolReported = ['submitted', 'acknowledged', 'not_required'].includes(
    input.schoolStatus ?? '',
  )
  const base = {
    required,
    inProgress: input.nilGoStatus === 'in_progress',
    confirmationNumber: confirmation,
    clearedAt: cleared ? iso(input.nilGoSubmittedAt) : null,
    schoolReported,
  }
  if (!required || input.nilGoStatus === 'not_required') {
    return { ...base, required: false, state: 'not_required', deadline: null, daysLeft: null }
  }
  if (cleared) return { ...base, state: 'cleared', deadline: null, daysLeft: null }
  if (!input.agreed) return { ...base, state: 'waiting', deadline: null, daysLeft: null }
  const signed = input.executedAt ? new Date(input.executedAt as any) : now
  const deadline = addBusinessDays(signed, NIL_GO_BUSINESS_DAYS)
  deadline.setHours(23, 59, 59, 999)
  const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / DAY) - 1
  return {
    ...base,
    state: deadline.getTime() < now.getTime() ? 'overdue' : 'due',
    deadline: deadline.toISOString(),
    daysLeft,
  }
}

const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''

/** The short status: "Cleared", "Due Oct 20", "2 days overdue", "After signing" */
export function nilGoLabel(c: NilCompliance) {
  if (c.state === 'cleared') return 'Cleared'
  if (c.state === 'waiting') return 'After signing'
  if (c.state === 'overdue') {
    const late = Math.abs(c.daysLeft ?? 0)
    return `${late} day${late === 1 ? '' : 's'} overdue`
  }
  if (c.daysLeft === 0) return 'Due today'
  return `Due ${shortDate(c.deadline)}`
}
